import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import { Button } from "primereact/button";
import { NidInteligenciaDadosPjProps } from "../../../interface/NidInteligenciaDadosPj";
import { formatarCNPJ as formatCNPJ } from '../../../utils/cnpj';

// Native SVG Pie Chart Component
const NativePieChart = ({ percentage, color, label }: { percentage: number; color: string; label: string }) => {
  const radius = 25;
  const strokeWidth = 50;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Midpoint angles in radians
  const orangeAngle = -Math.PI / 2 + (percentage / 100) * Math.PI;
  const grayAngle = Math.PI / 2 + (percentage / 100) * Math.PI;

  const distance = 26; // Distance from center for text placement
  const orangeX = 50 + distance * Math.cos(orangeAngle);
  const orangeY = 50 + distance * Math.sin(orangeAngle);

  const grayX = 50 + distance * Math.cos(grayAngle);
  const grayY = 50 + distance * Math.sin(grayAngle);

  const grayPercentage = Math.round((100 - percentage) * 10) / 10;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
      <div style={{ position: "relative", width: "150px", height: "150px" }}>
        <svg width="150" height="150" viewBox="0 0 100 100" style={{ overflow: "visible" }}>
          {/* Background circle (gray slice) */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="transparent"
            stroke="#E5E7EB"
            strokeWidth={strokeWidth}
          />
          {/* Value slice (orange slice) */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="transparent"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 50 50)"
            style={{
              transition: "stroke-dashoffset 0.8s ease-in-out",
            }}
          />
          {/* Labels inside slices */}
          {percentage > 5 && (
            <text
              x={orangeX}
              y={orangeY}
              fill="#FFFFFF"
              fontSize="6.5"
              fontWeight="800"
              textAnchor="middle"
              dominantBaseline="central"
            >
              {percentage}%
            </text>
          )}
          {grayPercentage > 5 && (
            <text
              x={grayX}
              y={grayY}
              fill="#6B7280"
              fontSize="6.5"
              fontWeight="800"
              textAnchor="middle"
              dominantBaseline="central"
            >
              {grayPercentage}%
            </text>
          )}
        </svg>
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "4px", fontSize: "0.75rem", color: "#6B7280" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: color }}></div>
          <span style={{ fontWeight: 600 }}>{label}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#E5E7EB" }}></div>
          <span style={{ fontWeight: 600 }}>Outros</span>
        </div>
      </div>
    </div>
  );
};

// Native SVG Semi-Circle Gauge Component
const NativeSemiCircleGauge = ({ percentage, color, label }: { percentage: number; color: string; label: string }) => {
  const radius = 35;
  const strokeWidth = 10;
  const circumference = Math.PI * radius; // Semi-circle length
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
      <div style={{ position: "relative", width: "120px", height: "65px" }}>
        <svg width="120" height="65" viewBox="0 0 100 55">
          {/* Background Arc */}
          <path
            d="M 15,50 A 35,35 0 0,1 85,50"
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          {/* Value Arc */}
          <path
            d="M 15,50 A 35,35 0 0,1 85,50"
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{
              transition: "stroke-dashoffset 0.8s ease-in-out",
            }}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            bottom: "0",
            left: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <span style={{ fontSize: "1rem", fontWeight: 800, color: "#1F2937", lineHeight: 1 }}>
            {percentage}%
          </span>
          <span style={{ fontSize: "0.75rem", color: "#9CA3AF", fontWeight: 600 }}>
            {label}
          </span>
        </div>
      </div>
    </div>
  );
};

export const NidInteligenciaDadosPj = ({ data }: NidInteligenciaDadosPjProps) => {
  const { t } = useTranslation();
  const [showAllSocios, setShowAllSocios] = useState(false);

  const rf = data?.data?.receitaFederalPJ || data?.receitaFederalPJ;
  const rec = data?.data?.recebimentosViaBoletoCartaoTedPJ || data?.recebimentosViaBoletoCartaoTedPJ || data?.data?.recebimentosViaBoletoPJ || data?.recebimentosViaBoletoPJ || (data as any)?.recebimentosBoletos?.data?.recebimentosViaBoletoPJ;
  const pag = data?.data?.pagamentosViaBoletoTedPJ || data?.pagamentosViaBoletoTedPJ || data?.data?.pagamentosViaBoletoCartaoTedPJ || data?.pagamentosViaBoletoCartaoTedPJ || data?.data?.pagamentosViaBoletoPJ || data?.pagamentosViaBoletoPJ || (data as any)?.pagamentosBoletos?.data?.pagamentosViaBoletoPJ;
  const concRec = data?.data?.concentracaoRecebimentoPJ || data?.concentracaoRecebimentoPJ || (data as any)?.concentracaoRecebimentos?.data?.concentracaoRecebimentoPJ;
  const concPag = data?.data?.concentracaoPagamentoPJ || data?.concentracaoPagamentoPJ || (data as any)?.concentracaoPagamentos?.data?.concentracaoPagamentoPJ;
  const atraso = data?.data?.mediaDiasInadimpleciaPJ || data?.mediaDiasInadimpleciaPJ || (data as any)?.indiceDiasAtraso?.data?.mediaDiasInadimpleciaPJ;
  const liqRec = data?.data?.liquidezRecebimentoPJ || data?.liquidezRecebimentoPJ || (data as any)?.liquidezRecebimentos?.data?.liquidezRecebimentoPJ;
  const liqPag = data?.data?.liquidezPagamentoPJ || data?.liquidezPagamentoPJ || (data as any)?.liquidezPagamentos?.data?.liquidezPagamentoPJ;
  const predRec = data?.data?.predicaoRecebimentoBoletoPJ || data?.predicaoRecebimentoBoletoPJ || (data as any)?.predicaoRecebimentosBoleto?.data?.predicaoRecebimentoBoletoPJ;
  const scoreEvObj = data?.data?.scoreEvolucaoPJ || data?.scoreEvolucaoPJ || (data as any)?.scoreEvolucao?.data?.scoreEvolucaoPJ;

  const resumo = data?.data?.resumoRetorno || data?.resumoRetorno || (data as any)?.pagamentosBoletos?.data?.resumoRetorno || (data as any)?.predicaoRecebimentosBoleto?.data?.resumoRetorno || (data as any)?.recebimentosBoletos?.data?.resumoRetorno || (data as any)?.scoreEvolucao?.data?.resumoRetorno;
  const dataConsulta = resumo?.dataConsulta;
  const documentNumber = resumo?.document;

  const getPct = (indicador: any) => {
    if (indicador === undefined || indicador === null) return null;
    const num = typeof indicador === "string" ? parseFloat(indicador) : indicador;
    if (isNaN(num)) return null;
    const multiplier = num <= 1 ? 100 : 1;
    return Math.round(num * multiplier * 10) / 10;
  };

  const recPct = getPct(concRec?.indicador);
  const pagPct = getPct(concPag?.indicador);
  const liqRecPct = getPct(liqRec?.indicador);
  const liqPagPct = getPct(liqPag?.indicador);

  const getAtrasoDays = (atrasoObj: any) => {
    if (atrasoObj?.indicador === undefined || atrasoObj?.indicador === null) return null;
    const num = typeof atrasoObj.indicador === "string" ? parseFloat(atrasoObj.indicador) : atrasoObj.indicador;
    return isNaN(num) ? null : num;
  };

  const atrasoDays = getAtrasoDays(atraso);

  if (!rf && !rec && !pag && !concRec && !concPag && atrasoDays === null && liqRecPct === null && liqPagPct === null && !predRec && !scoreEvObj) return null;

  // Format Helper Functions
  const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr === "0000-00-00") return "-";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const formatCurrency = (amount: number) => {
    if (amount === undefined || amount === null) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(amount);
  };

  const formatCEP = (cepStr: string) => {
    if (!cepStr) return "-";
    const clean = cepStr.replace(/\D/g, "");
    if (clean.length !== 8) return cepStr;
    return `${clean.slice(0, 5)}-${clean.slice(5)}`;
  };

  const getPeriodString = (dataConsulta?: string) => {
    if (!dataConsulta) return "Últimos 12 meses";
    const datePart = dataConsulta.split("|")[0].trim();
    const parts = datePart.split("/");
    if (parts.length !== 3) return "Últimos 12 meses";
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    
    const endMonthDate = new Date(year, month - 1, 1);
    const startMonthDate = new Date(year, month - 12, 1);
    
    const monthsShort = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
    
    const startStr = `${monthsShort[startMonthDate.getMonth()]}/${String(startMonthDate.getFullYear()).slice(-2)}`;
    const endStr = `${monthsShort[endMonthDate.getMonth()]}/${String(endMonthDate.getFullYear()).slice(-2)}`;
    
    return `${startStr} — ${endStr}`;
  };

  const getLastMonthString = (dataConsulta?: string) => {
    if (!dataConsulta) return "Último mês";
    const datePart = dataConsulta.split("|")[0].trim();
    const parts = datePart.split("/");
    if (parts.length !== 3) return "Último mês";
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    
    const endMonthDate = new Date(year, month - 1, 1);
    const monthsShort = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
    
    return `Último mês (${monthsShort[endMonthDate.getMonth()]}/${String(endMonthDate.getFullYear()).slice(-2)})`;
  };

  const getConcentrationConfig = (pct: number) => {
    if (pct > 70) {
      return {
        label: "Alto",
        color: "#EF4444",
        badgeStyle: { backgroundColor: "#FDE8E8", color: "#9B1C1C" },
        calloutStyle: { backgroundColor: "#FDF2F2", border: "1px solid #FDE8E8", color: "#9B1C1C" },
        iconColor: "#EF4444",
      };
    }
    if (pct > 30) {
      return {
        label: "Médio",
        color: "#F59E0B",
        badgeStyle: { backgroundColor: "#FEF3C7", color: "#92400E" },
        calloutStyle: { backgroundColor: "#FEFBF0", border: "1px solid #FEF3C7", color: "#B45309" },
        iconColor: "#F59E0B",
      };
    }
    return {
      label: "Baixo",
      color: "#10B981",
      badgeStyle: { backgroundColor: "#DEF7EC", color: "#03543F" },
      calloutStyle: { backgroundColor: "#F0FDF4", border: "1px solid #DCFCE7", color: "#15803D" },
      iconColor: "#10B981",
    };
  };

  const renderCalloutText = (pct: number, isRec: boolean) => {
    if (isRec) {
      if (pct > 70) {
        return (
          <span>
            Concentração <strong>alta ({pct}%)</strong>. Existe uma forte dependência da contraparte líder. Recomenda-se diversificação.
          </span>
        );
      }
      if (pct > 30) {
        return (
          <span>
            Concentração <strong>média ({pct}%)</strong>. Acompanhe a dependência da contraparte líder (Empresa com maior representatividade) e considere políticas de diversificação.
          </span>
        );
      }
      return (
        <span>
          Concentração <strong>baixa ({pct}%)</strong>. A dependência da contraparte líder é reduzida.
        </span>
      );
    } else {
      if (pct > 70) {
        return (
          <span>
            Concentração <strong>alta ({pct}%)</strong>. Seus desembolsos estão concentrados em poucos favorecidos.
          </span>
        );
      }
      if (pct > 30) {
        return (
          <span>
            Concentração <strong>média ({pct}%)</strong>. Seus desembolsos possuem concentração moderada.
          </span>
        );
      }
      return (
        <span>
          Concentração <strong>baixa ({pct}%)</strong>. Seus desembolsos estão bem distribuídos — a dependência de Favorecido com maior representatividade é reduzida.
        </span>
      );
    }
  };

  const renderConcentrationCard = (pct: number, isRec: boolean) => {
    const config = getConcentrationConfig(pct);
    const cardTitle = isRec ? "Concentração de Recebimentos" : "Concentração de Pagamentos";
    const cardDesc = isRec
      ? "Participação da maior contraparte (escala 0—0% · 1—100%) nos recebimentos do CNPJ analisado"
      : "Participação do principal favorecido (escala 0—0% · 1—100%) nos pagamentos do CNPJ analisado";
    const label = isRec ? "Empresa com maior representatividade" : "Favorecido com maior representatividade";

    return (
      <div style={{ border: "1px solid #E5E7EB", borderRadius: "12px", padding: "20px", display: "flex", flexDirection: "column", gap: "20px", backgroundColor: "#FFFFFF" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, color: "#1F2937" }}>{cardTitle}</span>
          <span style={{
            ...config.badgeStyle,
            fontSize: "0.75rem",
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: "4px"
          }}>
            {config.label}
          </span>
        </div>
        <span style={{ fontSize: "small", color: "#9CA3AF", lineHeight: "1.4" }}>
          {cardDesc}
        </span>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", alignItems: "center" }}>
          {/* Left Column: Pie Chart */}
          <div style={{ flex: "1 1 200px", display: "flex", justifyContent: "center" }}>
            <NativePieChart percentage={pct} color={config.color} label={label} />
          </div>

          {/* Right Column: Details & Callout */}
          <div style={{ flex: "1.5 1 250px", display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* Detail Row */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px", borderBottom: "1px solid #F3F4F6", paddingBottom: "12px" }}>
              <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: config.color }}></div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontWeight: 700, color: "#374151" }}>{label}</span>
                <span style={{ fontSize: "small", color: "#6B7280" }}>
                  {isRec ? "Maior contribuinte de recebimentos" : "Favorecido com maior participação"}
                </span>
              </div>
              <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <span style={{ fontWeight: 700, color: "#1F2937", fontSize: "1rem" }}>{pct}%</span>
                <span style={{ fontSize: "0.75rem", color: "#6B7280" }}>do total</span>
              </div>
            </div>

            {/* Callout Box */}
            <div style={{
              ...config.calloutStyle,
              borderRadius: "8px",
              padding: "12px 16px"
            }}>
              <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                <i className="pi pi-info-circle" style={{ color: config.iconColor, marginTop: "2px" }}></i>
                <span style={{ fontSize: "small", lineHeight: "1.5" }}>
                  {renderCalloutText(pct, isRec)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getInadimplenciaConfig = (days: number) => {
    const cappedDays = Math.round(days);

    if (days <= 10) {
      return {
        status: "OK",
        daysText: `${cappedDays} dias`,
        badgeText: `OK (${cappedDays} d)`,
        badgeStyle: { backgroundColor: "#DEF7EC", color: "#03543F", border: "1px solid #84E1BC" },
        progressPercent: Math.min(100, (days / 30) * 100),
      };
    }
    if (days < 20) {
      return {
        status: "Atenção",
        daysText: `${cappedDays} dias`,
        badgeText: `Atenção (${cappedDays} d)`,
        badgeStyle: { backgroundColor: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D" },
        progressPercent: Math.min(100, (days / 30) * 100),
      };
    }
    return {
      status: "Crítico",
      daysText: `${cappedDays} dias`,
      badgeText: `Crítico (${cappedDays} d)`,
      badgeStyle: { backgroundColor: "#FDE8E8", color: "#9B1C1C", border: "1px solid #F8B4B4" },
      progressPercent: Math.min(100, (days / 30) * 100),
    };
  };

  const renderInadimplenciaBlock = (days: number) => {
    const config = getInadimplenciaConfig(days);

    return (
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          border: "1px solid #E5E7EB",
          boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
          padding: "24px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h4 style={{ fontSize: "1rem", fontWeight: 700, margin: "0 0 4px 0", color: "#1F2937" }}>
              Média de dias de inadimplência
            </h4>
            <span style={{ fontSize: "small", color: "#6B7280", fontWeight: 500 }}>
              Quanto tempo, em média, esta empresa atrasa para realizar o pagamento para seus fornecedores.
            </span>
          </div>
          <span
            style={{
              ...config.badgeStyle,
              fontSize: "0.75rem",
              fontWeight: 800,
              padding: "4px 12px",
              borderRadius: "20px",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: config.badgeStyle.color }}></span>
            {config.badgeText}
          </span>
        </div>

        {/* Days display */}
        <div style={{ marginBottom: "20px" }}>
          <span style={{ fontSize: "1.5rem", fontWeight: 800, color: "#1F2937", marginRight: "6px" }}>
            {config.daysText.split(" ")[0]}
          </span>
          <span style={{ fontSize: "1rem", color: "#6B7280", fontWeight: 600 }}>
            dias
          </span>
        </div>

        {/* Custom Progress Bar */}
        <div style={{ position: "relative", width: "100%", height: "14px", borderRadius: "7px", background: "linear-gradient(to right, #34D399 0%, #FBBF24 50%, #F87171 100%)", marginBottom: "20px" }}>
          <div
            style={{
              position: "absolute",
              left: `${config.progressPercent}%`,
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: "6px",
              height: "22px",
              borderRadius: "3px",
              backgroundColor: "#374151",
              border: "1px solid #111827",
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              transition: "left 0.8s ease-in-out",
            }}
          />
        </div>

        {/* Legend row */}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", fontSize: "0.75rem", fontWeight: 600 }}>
          {/* OK Legend */}
          <span
            style={{
              backgroundColor: "#F0FDF4",
              border: "1px solid #BBF7D0",
              color: "#166534",
              padding: "4px 10px",
              borderRadius: "20px",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#166534" }}></span>
            OK (≤ 10d)
          </span>

          {/* Atenção Legend */}
          <span
            style={{
              backgroundColor: "#FFFBEB",
              border: "1px solid #FEF08A",
              color: "#B45309",
              padding: "4px 10px",
              borderRadius: "20px",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#B45309" }}></span>
            Atenção (&gt; 10 e &lt; 20d)
          </span>

          {/* Crítico Legend */}
          <span
            style={{
              backgroundColor: "#FDF2F2",
              border: "1px solid #FEE2E2",
              color: "#9B1C1C",
              padding: "4px 10px",
              borderRadius: "20px",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#9B1C1C" }}></span>
            Crítico (≥ 20d)
          </span>
        </div>
      </div>
    );
  };

  const getLiquidezConfig = (pct: number, isRec: boolean) => {
    if (pct >= 95) {
      return {
        label: "Excelente",
        color: "#10B981",
        badgeStyle: { backgroundColor: "#DEF7EC", color: "#03543F" },
        calloutStyle: { backgroundColor: "#F0FDF4", border: "1px solid #DCFCE7", color: "#15803D" },
        calloutText: isRec
          ? "O fluxo de recebimento está em nível muito saudável."
          : "A adimplência via boleto está em nível muito saudável."
      };
    }
    if (pct >= 85) {
      return {
        label: "Boa",
        color: "#3B82F6",
        badgeStyle: { backgroundColor: "#E0F2FE", color: "#0369A1" },
        calloutStyle: { backgroundColor: "#F0F9FF", border: "1px solid #E0F2FE", color: "#0369A1" },
        calloutText: isRec
          ? "Mantenha as políticas de cobrança e monitoramento."
          : "Os pagamentos ocorrem dentro do prazo esperado."
      };
    }
    if (pct >= 70) {
      return {
        label: "Regular",
        color: "#F59E0B",
        badgeStyle: { backgroundColor: "#FEF3C7", color: "#92400E" },
        calloutStyle: { backgroundColor: "#FEFBF0", border: "1px solid #FEF3C7", color: "#B45309" },
        calloutText: "Recomenda-se acompanhar de perto os fluxos de caixa."
      };
    }
    return {
      label: "Crítica",
      color: "#EF4444",
      badgeStyle: { backgroundColor: "#FDE8E8", color: "#9B1C1C" },
      calloutStyle: { backgroundColor: "#FDF2F2", border: "1px solid #FDE8E8", color: "#9B1C1C" },
      calloutText: "Alto índice de atraso no fluxo de pagamentos/recebimentos."
    };
  };

  const renderLiquidezCard = (pct: number, isRec: boolean) => {
    const config = getLiquidezConfig(pct, isRec);
    const cardTitle = isRec ? "Saúde Financeira - Recebimentos (Boleto)" : "Saúde Financeira - Pagamentos (Boleto)";
    const cardDesc = isRec
      ? "Percentual de boletos recebidos (quitados) sobre os boletos emitidos - Últimos 12 meses"
      : "Percentual de boletos pagos sobre os boletos emitidos - Últimos 12 meses";

    return (
      <div style={{ border: "1px solid #E5E7EB", borderRadius: "12px", padding: "20px", display: "flex", flexDirection: "column", gap: "20px", backgroundColor: "#FFFFFF" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, color: "#1F2937" }}>{cardTitle}</span>
          <span style={{
            ...config.badgeStyle,
            fontSize: "0.75rem",
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: "4px"
          }}>
            {config.label}
          </span>
        </div>
        <span style={{ fontSize: "small", color: "#9CA3AF", lineHeight: "1.4" }}>
          {cardDesc}
        </span>

        {/* Native Semi Circle Gauge */}
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}>
          <NativeSemiCircleGauge percentage={pct} color={config.color} label="Liquidez" />
        </div>

        {/* Callout */}
        <div style={{
          ...config.calloutStyle,
          borderRadius: "8px",
          padding: "12px 16px"
        }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: config.color }}></div>
            <span style={{ fontSize: "small", color: config.calloutStyle.color, lineHeight: "1.5" }}>
              Liquidez <b>{config.label.toLowerCase()} ({pct}%)</b>. {config.calloutText}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderRecebimentosCard = (recObj: any) => {
    const recTotal = recObj?.indicador ? parseFloat(recObj.indicador) : 0;
    const recMonth = recTotal / 12;
    const period = getPeriodString(dataConsulta);
    const lastMonth = getLastMonthString(dataConsulta);

    const recTitle = recObj?.segmentacao === "boletoRecebimentoValor12M"
      ? "Recebimentos via Boleto — Últimos 12 meses"
      : "Boleto + TED + Cartão — Últimos 12 meses";

    const recDesc = recObj?.segmentacao === "boletoRecebimentoValor12M"
      ? `Soma dos recebimentos no período ${period}.`
      : `Soma dos recebimentos do período (${period}).`;

    return (
      <div
        style={{
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          padding: "20px",
          backgroundColor: "#FBFDFE",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
          <span
            style={{
              backgroundColor: "#EBF5FF",
              color: "#1E40AF",
              fontSize: "0.75rem",
              fontWeight: 800,
              padding: "2px 8px",
              borderRadius: "4px",
              textTransform: "uppercase",
            }}
          >
            Recebimentos Totais
          </span>
          <span style={{ fontSize: "0.75rem", color: "#9CA3AF", fontWeight: 500 }}>
            Período: {period}
          </span>
        </div>
        <h4 style={{ fontWeight: 700, color: "#1F2937", margin: "0 0 16px 0" }}>
          {recTitle}
        </h4>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
              <span style={{ fontSize: "small", color: "#6B7280", fontWeight: 500 }}>Total (12 meses)</span>
              <span style={{ backgroundColor: "#D1FAE5", color: "#065F46", fontSize: "0.75rem", fontWeight: 700, padding: "1px 4px", borderRadius: "3px" }}>
                Consolidado
              </span>
            </div>
            <span style={{ fontSize: "1.1rem", fontWeight: 800, color: "#1E3A8A", display: "block" }}>
              {formatCurrency(recTotal)}
            </span>
            <span style={{ fontSize: "0.75rem", color: "#9CA3AF", display: "block", marginTop: "4px" }}>
              {recDesc}
            </span>
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
              <span style={{ fontSize: "small", color: "#6B7280", fontWeight: 500 }}>{lastMonth}</span>
              <span style={{ backgroundColor: "#F3F4F6", color: "#374151", fontSize: "0.75rem", fontWeight: 700, padding: "1px 4px", borderRadius: "3px" }}>
                Estimado
              </span>
            </div>
            <span style={{ fontSize: "1.1rem", fontWeight: 800, color: "#1E3A8A", display: "block" }}>
              {formatCurrency(recMonth)}
            </span>
            <span style={{ fontSize: "0.75rem", color: "#9CA3AF", display: "block", marginTop: "4px" }}>
              Valor do mês mais recente do período.
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderPagamentosCard = (pagObj: any) => {
    const pagTotal = pagObj?.indicador ? parseFloat(pagObj.indicador) : 0;
    const pagMonth = pagTotal / 12;
    const period = getPeriodString(dataConsulta);
    const lastMonth = getLastMonthString(dataConsulta);
    const pagTitle = pagObj?.segmentacao === "boletoPagamentoValor12M" ? "Pagamentos via Boleto — Últimos 12 meses" : "Pagamentos via Boleto + TED — Últimos 12 meses";

    return (
      <div
        style={{
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          padding: "20px",
          backgroundColor: "#FFFDFD",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
          <span
            style={{
              backgroundColor: "#FDF2F2",
              color: "#9B1C1C",
              fontSize: "0.75rem",
              fontWeight: 800,
              padding: "2px 8px",
              borderRadius: "4px",
              textTransform: "uppercase",
            }}
          >
            Pagamentos
          </span>
          <span style={{ fontSize: "0.75rem", color: "#9CA3AF", fontWeight: 500 }}>
            Período: {period}
          </span>
        </div>
        <h4 style={{ fontWeight: 700, color: "#1F2937", margin: "0 0 16px 0" }}>
          {pagTitle}
        </h4>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
              <span style={{ fontSize: "small", color: "#6B7280", fontWeight: 500 }}>Total pago (12 meses)</span>
              <span style={{ backgroundColor: "#FEE2E2", color: "#991B1B", fontSize: "0.75rem", fontWeight: 700, padding: "1px 4px", borderRadius: "3px" }}>
                Saída
              </span>
            </div>
            <span style={{ fontSize: "1.1rem", fontWeight: 800, color: "#B91C1C", display: "block" }}>
              {formatCurrency(pagTotal)}
            </span>
            <span style={{ fontSize: "0.75rem", color: "#9CA3AF", display: "block", marginTop: "4px" }}>
              Soma de boletos quitados no período ({period}).
            </span>
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
              <span style={{ fontSize: "small", color: "#6B7280", fontWeight: 500 }}>{lastMonth}</span>
              <span style={{ backgroundColor: "#F3F4F6", color: "#374151", fontSize: "0.75rem", fontWeight: 700, padding: "1px 4px", borderRadius: "3px" }}>
                Estimado
              </span>
            </div>
            <span style={{ fontSize: "1.1rem", fontWeight: 800, color: "#B91C1C", display: "block" }}>
              {formatCurrency(pagMonth)}
            </span>
            <span style={{ fontSize: "0.75rem", color: "#9CA3AF", display: "block", marginTop: "4px" }}>
              Desembolso do mês mais recente do período.
            </span>
          </div>
        </div>
      </div>
    );
  };

  const getNextMonthStrings = (dataConsulta?: string) => {
    if (!dataConsulta) return { short: "Próximo mês", long: "próximo mês" };
    const datePart = dataConsulta.split("|")[0].trim();
    const parts = datePart.split("/");
    if (parts.length !== 3) return { short: "Próximo mês", long: "próximo mês" };
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    
    const nextMonthDate = new Date(year, month + 1, 1);
    
    const monthsShort = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
    const monthsLong = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
    
    const shortStr = `${monthsShort[nextMonthDate.getMonth()]}/${String(nextMonthDate.getFullYear()).slice(-2)}`;
    const longStr = `${monthsLong[nextMonthDate.getMonth()]} de ${nextMonthDate.getFullYear()}`;
    
    return { short: shortStr, long: longStr };
  };

  const renderPredicaoBlock = (predObj: any) => {
    const nextMonths = getNextMonthStrings(dataConsulta);
    const value = predObj?.indicador ? parseFloat(predObj.indicador) : 0;
    const isRec = predObj?.segmentacao?.toLowerCase().includes("recebimento") ?? true;
    const title = isRec ? "Recebimentos previstos via boleto" : "Pagamentos previstos via boleto";
    const desc = isRec
      ? "Estimativa de entradas financeiras com base em comportamento recente."
      : "Estimativa de saídas financeiras com base em comportamento recente.";

    return (
      <div
        style={{
          display: "flex",
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          overflow: "hidden",
          flexWrap: "wrap",
          backgroundColor: "#FFFFFF",
        }}
      >
        <div
          style={{
            padding: "20px",
            backgroundColor: "#F9FAFB",
            borderRight: "1px solid #E5E7EB",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            flex: "1 1 200px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", marginBottom: "8px", justifyContent: "space-between" }}>
            <span style={{ backgroundColor: "#D1FAE5", color: "#065F46", fontSize: "0.75rem", fontWeight: 800, padding: "2px 8px", borderRadius: "4px" }}>
              PRÓXIMO MÊS
            </span>
            <span style={{ fontSize: "small", color: "#9CA3AF", fontWeight: 700 }}>{nextMonths.short}</span>
          </div>
          <span style={{ fontWeight: 700, color: "#1F2937" }}>
            {title}
          </span>
          <span style={{ fontSize: "0.75rem", color: "#9CA3AF", marginTop: "4px" }}>
            {desc}
          </span>
        </div>

        <div style={{ padding: "20px", flex: "2 1 400px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <span style={{ fontSize: "small", color: "#6B7280", fontWeight: 600 }}>Valor previsto — {nextMonths.long}</span>
          <span style={{ fontSize: "1.25rem", fontWeight: 800, color: "#059669", margin: "4px 0" }}>
            {formatCurrency(value)}
          </span>
          <span style={{ fontSize: "0.75rem", color: "#9CA3AF", lineHeight: "1.5" }}>
            Esta é uma <strong>estimativa</strong> e pode variar conforme sazonalidade, inadimplência e políticas de cobrança. Combine com pipeline e taxa de conversão recentes para maior precisão.
          </span>
        </div>
      </div>
    );
  };

  const getScoreEvolucaoConfig = (score: number) => {
    const percent = Math.round((score / 1000) * 100);
    if (score >= 850) {
      return {
        label: "Excelente",
        color: "#10B981",
        badgeStyle: { backgroundColor: "#DEF7EC", color: "#03543F" },
        calloutStyle: { backgroundColor: "#F0FDF4", border: "1px solid #DCFCE7", color: "#15803D" },
        probabilityText: "muito alta",
      };
    }
    if (score >= 700) {
      return {
        label: "Bom",
        color: "#3B82F6",
        badgeStyle: { backgroundColor: "#E0F2FE", color: "#0369A1" },
        calloutStyle: { backgroundColor: "#F0F9FF", border: "1px solid #E0F2FE", color: "#0369A1" },
        probabilityText: "alta",
      };
    }
    if (score >= 500) {
      return {
        label: "Regular",
        color: "#F59E0B",
        badgeStyle: { backgroundColor: "#FEF3C7", color: "#92400E" },
        calloutStyle: { backgroundColor: "#FEFBF0", border: "1px solid #FEF3C7", color: "#B45309" },
        probabilityText: "média",
      };
    }
    return {
      label: "Ruim",
      color: "#EF4444",
      badgeStyle: { backgroundColor: "#FDE8E8", color: "#9B1C1C" },
      calloutStyle: { backgroundColor: "#FDF2F2", border: "1px solid #FDE8E8", color: "#9B1C1C" },
      probabilityText: "baixa",
    };
  };

  const renderScoreEvolucaoBlock = (scoreObj: any) => {
    const score = scoreObj?.score ? parseInt(scoreObj.score) : 0;
    const config = getScoreEvolucaoConfig(score);
    const percent = Math.round((score / 1000) * 100);
    const positionPercent = score / 10;

    return (
      <div style={{ marginTop: "8px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", alignItems: "center" }}>
          {/* Progress bar column */}
          <div style={{ flex: "1.2 1 300px", position: "relative" }}>
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "14px",
                borderRadius: "7px",
                background: "linear-gradient(to right, #EF4444 0%, #EF4444 50%, #F59E0B 50%, #F59E0B 70%, #3B82F6 70%, #3B82F6 85%, #10B981 85%, #10B981 100%)",
                marginTop: "36px",
                marginBottom: "8px",
              }}
            >
              {/* Pointer & Bubble */}
              <div
                style={{
                  position: "absolute",
                  left: `${positionPercent}%`,
                  bottom: "100%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginBottom: "4px",
                }}
              >
                {/* Bubble */}
                <div
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "4px",
                    padding: "2px 6px",
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    color: "#374151",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {score}/1000
                </div>
                {/* Triangle pointer */}
                <svg width="10" height="6" viewBox="0 0 10 6" style={{ display: "block", marginTop: "-1px" }}>
                  <path d="M0 0 L5 6 L10 0 Z" fill={config.color} />
                </svg>
              </div>
            </div>

            {/* Labels */}
            <div style={{ position: "relative", width: "100%", display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "#9CA3AF", fontWeight: 600 }}>
              <span>0</span>
              <span style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>500</span>
              <span>1000</span>
            </div>
          </div>

          {/* Callout column */}
          <div style={{ flex: "1 1 250px" }}>
            <div
              style={{
                ...config.calloutStyle,
                borderRadius: "12px",
                padding: "16px 20px",
                display: "flex",
                gap: "12px",
                alignItems: "center",
              }}
            >
              <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: config.color, flexShrink: 0 }} />
              <span style={{ fontSize: "small", lineHeight: "1.5", color: config.calloutStyle.color }}>
                Probabilidade <strong>{config.probabilityText}</strong> de pagamento em dia dos boletos nos <strong>próximos 90 dias</strong> ({score}/1000 · {percent}%).
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isOnlyTransacionado = !rf && (!!rec || !!pag);

  const isOnlyConcentracao = !rf && !rec && !pag && (recPct !== null || pagPct !== null);

  const isOnlyInadimplencia = !rf && !rec && !pag && !concRec && !concPag && (atrasoDays !== null);

  const isOnlyLiquidez = !rf && !rec && !pag && !concRec && !concPag && (atrasoDays === null) && (liqRecPct !== null || liqPagPct !== null);

  const isOnlyPredicao = !rf && !rec && !pag && !concRec && !concPag && (atrasoDays === null) && (liqRecPct === null && liqPagPct === null) && (predRec !== null && predRec !== undefined);

  const isOnlyScoreEvolucao = !rf && !rec && !pag && !concRec && !concPag && (atrasoDays === null) && (liqRecPct === null && liqPagPct === null) && !predRec && (scoreEvObj !== null && scoreEvObj !== undefined);

  if (isOnlyScoreEvolucao) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", color: "#1F2937" }}>
        {/* Score de Risco Block */}
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
            padding: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
              Score de Risco
            </h3>
            <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
          </div>
          <p style={{ color: "#6B7280", margin: "0 0 20px 0", lineHeight: "1.6" }}>
            Mede a probabilidade de a empresa honrar seus compromissos financeiros nos próximos 90 dias por meio do pagamento de seus boletos de forma pontual.
          </p>

          {renderScoreEvolucaoBlock(scoreEvObj)}
        </div>
      </div>
    );
  }

  if (isOnlyPredicao) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", color: "#1F2937" }}>
        {/* Predição de Valor Transacionado Block */}
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
            padding: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
              Predição de Valor Transacionado
            </h3>
            <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
          </div>
          <p style={{ color: "#6B7280", margin: "0 0 20px 0", lineHeight: "1.6" }}>
            Projeta o valor médio mensal de pagamentos ou recebimentos de uma empresa para os próximos 12 meses. Utilizando modelagem com inteligência artificial e baseado no histórico de transações da empresa por boletos, cartão e TED.
          </p>

          {renderPredicaoBlock(predRec)}
        </div>
      </div>
    );
  }

  if (isOnlyLiquidez) {
    const showRec = liqRecPct !== null;
    const showPag = liqPagPct !== null;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", color: "#1F2937" }}>
        {/* Saúde Financeira Block */}
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
            padding: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
              Saúde Financeira
            </h3>
            <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
          </div>
          <p style={{ color: "#6B7280", margin: "0 0 24px 0", lineHeight: "1.6" }}>
            Indica como está a liquidez de recebimento de uma empresa, avaliando o valor dos boletos a receber/a pagar que estão sendo efetivamente recebidos/pagos em até 5 dias após a data de vencimento.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: showRec && showPag ? "repeat(auto-fit, minmax(320px, 1fr))" : "1fr", gap: "24px" }}>
            {showRec && renderLiquidezCard(liqRecPct!, true)}
            {showPag && renderLiquidezCard(liqPagPct!, false)}
          </div>
        </div>
      </div>
    );
  }

  if (isOnlyInadimplencia) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", color: "#1F2937" }}>
        {/* Title */}
        <div style={{ borderBottom: "1px solid #E5E7EB", paddingBottom: "12px", marginBottom: "8px" }}>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, margin: 0, color: "#1E3A8A", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Inadimplência
          </h2>
        </div>

        {renderInadimplenciaBlock(atrasoDays!)}
      </div>
    );
  }

  if (isOnlyConcentracao) {
    const showRec = recPct !== null;
    const showPag = pagPct !== null;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", color: "#1F2937" }}>
        {/* Maior Concentração Block */}
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
            padding: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
              Maior Concentração
            </h3>
            <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
          </div>
          <p style={{ color: "#6B7280", margin: "0 0 24px 0", lineHeight: "1.6" }}>
            Indica como está a concentração de pagamentos ou recebimentos de boletos para um CNPJ em relação aos seus fornecedores ou clientes. Este índice foi criado com base no conceitual econômico para medir o grau de concorrência de uma empresa.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: showRec && showPag ? "repeat(auto-fit, minmax(320px, 1fr))" : "1fr", gap: "24px" }}>
            {showRec && renderConcentrationCard(recPct!, true)}
            {showPag && renderConcentrationCard(pagPct!, false)}
          </div>
        </div>
      </div>
    );
  }

  if (isOnlyTransacionado) {
    const showRec = !!rec;
    const showPag = !!pag;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", color: "#1F2937" }}>
        {/* Valor Transacionado Block */}
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
            padding: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
              Valor Transacionado
            </h3>
            <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
          </div>

          <p style={{ color: "#6B7280", margin: "0 0 20px 0", lineHeight: "1.6" }}>
            Atribui a uma empresa um valor médio de recebimento ou pagamento em grupo de mesmo perfil ao longo de um período. Na composição desse grupo são consideradas diversas variáveis atreladas ao CNPJ consultado, sendo as principais: região demográfica, setor de mercado (CNAE) e valor de recebimento (porte).
          </p>

          <div style={{ display: "grid", gridTemplateColumns: showRec && showPag ? "repeat(auto-fit, minmax(320px, 1fr))" : "1fr", gap: "20px" }}>
            {showRec && renderRecebimentosCard(rec)}
            {showPag && renderPagamentosCard(pag)}
          </div>
        </div>
      </div>
    );
  }

  if (!rf) return null;

  // Badge/Chip styles
  const getBadgeStyle = (val: boolean) => ({
    backgroundColor: val ? "#DEF7EC" : "#FDE8E8",
    color: val ? "#03543F" : "#9B1C1C",
    fontWeight: 700,
    fontSize: "small",
    padding: "4px 10px",
    borderRadius: "6px",
    display: "inline-block",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", color: "#1F2937" }}>

      {/* 2. Dados da Receita Federal Card */}
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          border: "1px solid #E5E7EB",
          boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
          padding: "24px",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px", borderBottom: "1px solid #F3F4F6", paddingBottom: "16px" }}>
          <div
            style={{
              backgroundColor: "#EFF6FF",
              borderRadius: "10px",
              padding: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="pi pi-building" style={{ color: "#2563EB", fontSize: "24px" }}></i>
          </div>
          <div>
            <h2 style={{ fontSize: "1.1rem", fontWeight: 700, margin: 0, color: "#1E3A8A" }}>
              Dados da Receita Federal
            </h2>
            <span style={{ fontSize: "small", color: "#6B7280", fontWeight: 500 }}>
              Informações oficiais de cadastro da empresa
            </span>
          </div>
        </div>

        {/* Data Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px", marginBottom: "24px" }}>

          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span className="label-dados" style={{ color: "#9CA3AF", textTransform: "uppercase", paddingLeft: 0 }}>Razão Social</span>
            <span style={{ fontWeight: 700, color: "#1F2937" }}>{rf.razaoSocial || "-"}</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span className="label-dados" style={{ color: "#9CA3AF", textTransform: "uppercase", paddingLeft: 0 }}>CNPJ</span>
            <span style={{ fontWeight: 700, color: "#1F2937" }}>{formatCNPJ(rf.cnpj)}</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span className="label-dados" style={{ color: "#9CA3AF", textTransform: "uppercase", paddingLeft: 0 }}>Natureza Jurídica</span>
            <span style={{ fontWeight: 600, color: "#374151" }}>{rf.naturezaJuridica || "-"}</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span className="label-dados" style={{ color: "#9CA3AF", textTransform: "uppercase", paddingLeft: 0 }}>Data de Fundação</span>
            <span style={{ fontWeight: 600, color: "#374151" }}>
              <i className="pi pi-calendar" style={{ marginRight: "6px", color: "#6B7280" }}></i>
              {formatDate(rf.dataFundacao)}
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span className="label-dados" style={{ color: "#9CA3AF", textTransform: "uppercase", paddingLeft: 0 }}>Capital Social</span>
            <span style={{ fontWeight: 700, color: "#059669" }}>
              {formatCurrency(rf.capitalSocial)}
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span className="label-dados" style={{ color: "#9CA3AF", textTransform: "uppercase", paddingLeft: 0 }}>Porte da Empresa</span>
            <span style={{ fontWeight: 600, color: "#374151" }}>{rf.porteEmpresa || "-"}</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span className="label-dados" style={{ color: "#9CA3AF", textTransform: "uppercase", paddingLeft: 0 }}>Opção Simples</span>
            <div>
              <span style={getBadgeStyle(rf.opcaoSimples)}>
                {rf.opcaoSimples ? "Sim" : "Não"}
              </span>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span className="label-dados" style={{ color: "#9CA3AF", textTransform: "uppercase", paddingLeft: 0 }}>Opção MEI</span>
            <div>
              <span style={getBadgeStyle(rf.opcaoMei)}>
                {rf.opcaoMei ? "Sim" : "Não"}
              </span>
            </div>
          </div>

        </div>

        {/* Address footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            padding: "12px 18px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <i className="pi pi-map-marker" style={{ color: "#3B82F6", fontSize: "16px" }}></i>
            <span style={{ fontSize: "small", fontWeight: 600, color: "#4B5563" }}>
              {rf.endereco ? `${rf.endereco} - ${rf.bairro} - ${rf.municipio}/${rf.uf} - CEP ${formatCEP(rf.cep)}` : `${rf.logradouro}, ${rf.numero} ${rf.complemento ? `- ${rf.complemento}` : ""} - ${rf.bairro} - ${rf.cidade}/${rf.uf} - CEP ${formatCEP(rf.cep)}`}
            </span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              icon="pi pi-copy"
              className="p-button-rounded p-button-secondary p-button-text"
              tooltip="Copiar endereço"
              style={{ width: "32px", height: "32px", color: "#6B7280" }}
              onClick={() => {
                const text = rf.endereco || `${rf.logradouro}, ${rf.numero} - ${rf.bairro} - ${rf.cidade}/${rf.uf}`;
                navigator.clipboard.writeText(text);
              }}
            />
            <Button
              icon="pi pi-external-link"
              className="p-button-rounded p-button-secondary p-button-text"
              tooltip="Ver no Google Maps"
              style={{ width: "32px", height: "32px", color: "#6B7280" }}
              onClick={() => {
                const query = encodeURIComponent(rf.endereco || `${rf.logradouro}, ${rf.numero}, ${rf.cidade}`);
                window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank");
              }}
            />
          </div>
        </div>
      </div>

      {/* 3. SÓCIOS */}
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          border: "1px solid #E5E7EB",
          boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
          padding: "24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "18px" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
            Sócios
          </h3>
          <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
        </div>

        <div style={{ overflowX: "auto", borderRadius: "10px", border: "1px solid #F3F4F6", marginBottom: "16px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ backgroundColor: "#F3F4F6", borderBottom: "1px solid #E5E7EB" }}>
                <th style={{ padding: "12px 16px", fontWeight: 700, color: "#6B7280", width: "60px" }}>N°</th>
                <th style={{ padding: "12px 16px", fontWeight: 700, color: "#6B7280" }}>NOME</th>
                <th style={{ padding: "12px 16px", fontWeight: 700, color: "#6B7280" }}>CPF</th>
                <th style={{ padding: "12px 16px", fontWeight: 700, color: "#6B7280" }}>CARGO</th>
                <th style={{ padding: "12px 16px", fontWeight: 700, color: "#6B7280", textAlign: "center" }}>PART. (%)</th>
                <th style={{ padding: "12px 16px", fontWeight: 700, color: "#6B7280", textAlign: "center" }}>RESTRIÇÃO</th>
              </tr>
            </thead>
            <tbody>
              {rf.socios && rf.socios.length > 0 ? (
                (showAllSocios ? rf.socios : rf.socios.slice(0, 2)).map((socio, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: "1px solid #F3F4F6",
                      transition: "background-color 0.15s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F9FAFB")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <td style={{ padding: "14px 16px", fontWeight: 600, color: "#9CA3AF" }}>{idx + 1}</td>
                    <td style={{ padding: "14px 16px", fontWeight: 700, color: "#1F2937" }}>{socio.nome}</td>
                    <td style={{ padding: "14px 16px", color: "#4B5563" }}>{socio.cpf}</td>
                    <td style={{ padding: "14px 16px", color: "#4B5563" }}>{socio.cargo}</td>
                    <td style={{ padding: "14px 16px", color: "#9CA3AF", textAlign: "center" }}>-</td>
                    <td style={{ padding: "14px 16px", color: "#9CA3AF", textAlign: "center" }}>-</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ padding: "20px", textAlign: "center", color: "#9CA3AF" }}>
                    Nenhum sócio informado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {rf.socios && rf.socios.length > 2 && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              label={showAllSocios ? "Ver menos informações" : "Ver mais informações"}
              icon={showAllSocios ? "pi pi-chevron-up" : "pi pi-chevron-down"}
              className="p-button-outlined p-button-secondary p-button-sm"
              style={{
                borderRadius: "20px",
                fontWeight: 600,
                color: "#4B5563",
                borderColor: "#D1D5DB",
                padding: "6px 20px",
              }}
              onClick={() => setShowAllSocios(!showAllSocios)}
            />
          </div>
        )}
      </div>

      {/* 4. VALOR TRANSACIONADO */}
      {(!!rec || !!pag) && (
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
            padding: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
              Valor Transacionado
            </h3>
            <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
          </div>
          <p style={{ color: "#6B7280", margin: "0 0 20px 0", lineHeight: "1.6" }}>
            Atribui a uma empresa um valor médio de recebimento ou pagamento em grupo de mesmo perfil ao longo de um período. Na composição desse grupo são consideradas diversas variáveis atreladas ao CNPJ consultado, sendo as principais: região demográfica, setor de mercado (CNAE) e valor de recebimento (porte).
          </p>

          <div style={{ display: "grid", gridTemplateColumns: rec && pag ? "repeat(auto-fit, minmax(320px, 1fr))" : "1fr", gap: "20px" }}>
            {rec && renderRecebimentosCard(rec)}
            {pag && renderPagamentosCard(pag)}
          </div>
        </div>
      )}

      {/* 5. PREDIÇÃO DE VALOR TRANSACIONADO */}
      {predRec && (
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
            padding: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
              Predição de Valor Transacionado
            </h3>
            <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
          </div>
          <p style={{ color: "#6B7280", margin: "0 0 20px 0", lineHeight: "1.6" }}>
            Projeta o valor médio mensal de pagamentos ou recebimentos de uma empresa para os próximos 12 meses. Utilizando modelagem com inteligência artificial e baseado no histórico de transações da empresa por boletos, cartão e TED.
          </p>

          {renderPredicaoBlock(predRec)}
        </div>
      )}

      {/* SCORE DE RISCO */}
      {scoreEvObj && (
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
            padding: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
              Score de Risco
            </h3>
            <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
          </div>
          <p style={{ color: "#6B7280", margin: "0 0 20px 0", lineHeight: "1.6" }}>
            Mede a probabilidade de a empresa honrar seus compromissos financeiros nos próximos 90 dias por meio do pagamento de seus boletos de forma pontual.
          </p>

          {renderScoreEvolucaoBlock(scoreEvObj)}
        </div>
      )}

      {/* 6. MAIOR CONCENTRAÇÃO */}
      {(recPct !== null || pagPct !== null) && (
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
            padding: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
              Maior Concentração
            </h3>
            <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
          </div>
          <p style={{ color: "#6B7280", margin: "0 0 24px 0", lineHeight: "1.6" }}>
            Indica como está a concentração de pagamentos ou recebimentos de boletos para um CNPJ em relação aos seus fornecedores ou clientes. Este índice foi criado com base no conceitual econômico para medir o grau de concorrência de uma empresa.
          </p>

          <div style={{
            display: "grid",
            gridTemplateColumns: recPct !== null && pagPct !== null ? "repeat(auto-fit, minmax(320px, 1fr))" : "1fr",
            gap: "24px"
          }}>
            {recPct !== null && renderConcentrationCard(recPct, true)}
            {pagPct !== null && renderConcentrationCard(pagPct, false)}
          </div>
        </div>
      )}

      {/* INADIMPLÊNCIA */}
      {atrasoDays !== null && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "4px" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
              Inadimplência
            </h3>
            <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
          </div>
          {renderInadimplenciaBlock(atrasoDays)}
        </div>
      )}

      {/* 7. SAÚDE FINANCEIRA */}
      {(liqRecPct !== null || liqPagPct !== null) && (
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
            padding: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0 }}>
              Saúde Financeira
            </h3>
            <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}></div>
          </div>
          <p style={{ color: "#6B7280", margin: "0 0 24px 0", lineHeight: "1.6" }}>
            Indica como está a liquidez de recebimento de uma empresa, avaliando o valor dos boletos a receber/a pagar que estão sendo efetivamente recebidos/pagos em até 5 dias após a data de vencimento.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: liqRecPct !== null && liqPagPct !== null ? "repeat(auto-fit, minmax(320px, 1fr))" : "1fr", gap: "24px" }}>
            {liqRecPct !== null && renderLiquidezCard(liqRecPct, true)}
            {liqPagPct !== null && renderLiquidezCard(liqPagPct, false)}
          </div>
        </div>
      )}

    </div>
  );
};
